import { useEffect, useState, useCallback } from "react";
import { api, apiError, fmtINR } from "../../lib/api";
import { PageHeader } from "../../components/Layout";
import { StatusBadge } from "../../components/StatusBadge";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Briefcase, Clock, CheckCircle, Bell, Warning, Star, FileText } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const Stat = ({ icon: Icon, label, value, accent }) => (
  <Card className="rounded-sm p-6 border-border" data-testid={`pstat-${label.toLowerCase().replace(/ /g, "-")}`}>
    <div className="flex items-start justify-between">
      <div><p className="label-caps">{label}</p><p className="font-heading text-3xl font-bold mt-2">{value}</p></div>
      <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${accent}`}><Icon size={20} weight="bold" /></div>
    </div>
  </Card>
);

export default function PartnerDashboard() {
  const [d, setD] = useState(null);
  const [sell, setSell] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(() => { api.get("/partner/dashboard").then((r) => setD(r.data)); }, []);
  useEffect(() => { load(); api.get("/partner/sell-dashboard").then((r) => setSell(r.data)).catch(() => {}); }, [load]);

  const accept = async (jid) => {
    try {
      await api.post(`/partner/jobs/${jid}/status`, { status: "accepted" });
      toast.success("Job accepted");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (!d) return <div className="p-8 text-muted-foreground">Loading…</div>;

  return (
    <>
      <PageHeader title={`Welcome, ${d.partner.name}`} subtitle={`${d.partner.category || "Partner"} · ${d.partner.city || ""}`}
        actions={<StatusBadge value={d.partner.status} testid="partner-status-badge" />} />
      <div className="p-8 space-y-8">
        {d.awaiting_acceptance.length > 0 && (
          <div className="border-2 border-amber-300 bg-amber-50 rounded-sm p-5" data-testid="awaiting-banner">
            <div className="flex items-center gap-2 text-amber-800 font-semibold mb-3">
              <Warning size={20} weight="fill" />
              {d.awaiting_acceptance.length} job{d.awaiting_acceptance.length > 1 ? "s" : ""} awaiting your acceptance
            </div>
            <div className="space-y-2">
              {d.awaiting_acceptance.map((j) => (
                <div key={j.id} className="flex items-center justify-between bg-card border border-amber-200 rounded-sm px-4 py-3">
                  <div>
                    <p className="font-semibold">{j.service_type} · {j.product_category} {j.product_type && `(${j.product_type})`}</p>
                    <p className="text-xs text-muted-foreground">{j.customer_name} · {j.scheduled_date || "no date"} · {fmtINR(j.amount)}</p>
                  </div>
                  <Button size="sm" className="rounded-sm" onClick={() => accept(j.id)} data-testid={`accept-job-${j.id}`}>Accept</Button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <Stat icon={Briefcase} label="Total Jobs" value={d.total_jobs} accent="bg-blue-100 text-blue-700" />
          <Stat icon={Warning} label="Pending Acceptance" value={d.pending_acceptance} accent="bg-amber-100 text-amber-700" />
          <Stat icon={Clock} label="In Progress" value={d.in_progress} accent="bg-violet-100 text-violet-700" />
          <Stat icon={CheckCircle} label="Completed" value={d.completed} accent="bg-green-100 text-green-700" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="rounded-sm p-6 border-border" data-testid="pstat-rating">
            <div className="flex items-start justify-between">
              <div>
                <p className="label-caps">My Rating</p>
                <p className="font-heading text-3xl font-bold mt-2 flex items-center gap-2">
                  {d.avg_rating || "—"}
                  {d.avg_rating > 0 && <span className="inline-flex">{[1,2,3,4,5].map((i) => <Star key={i} size={18} weight={i <= Math.round(d.avg_rating) ? "fill" : "regular"} className={i <= Math.round(d.avg_rating) ? "text-amber-500" : "text-muted-foreground/40"} />)}</span>}
                </p>
                <p className="text-xs text-muted-foreground mt-1">{d.rating_count || 0} customer rating{d.rating_count === 1 ? "" : "s"}</p>
              </div>
              <div className="w-10 h-10 rounded-sm flex items-center justify-center bg-amber-100 text-amber-700"><Star size={20} weight="bold" /></div>
            </div>
          </Card>
          <div onClick={() => navigate("/portal/documents")} className="cursor-pointer">
            <Stat icon={FileText} label="My Documents" value={d.documents_count || 0} accent="bg-blue-100 text-blue-700" />
          </div>
        </div>

        {sell && sell.offers_sell_devices && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="sell-dashboard">
            <Card className={`rounded-sm p-6 ${sell.low_balance ? "border-2 border-red-300 bg-red-50" : "border-border"}`} data-testid="sell-wallet-card">
              <p className="label-caps">Sell Deposit Balance</p>
              <p className="font-heading text-3xl font-bold mt-2">{fmtINR(sell.deposit_balance)}</p>
              <p className="text-xs text-muted-foreground mt-1">Fee {sell.fee_type === "percent" ? `${sell.fee_value}%/pickup` : `${fmtINR(sell.fee_value)}/pickup`}</p>
              {sell.low_balance && <p className="text-xs text-red-600 font-semibold mt-1">⚠ Low balance — pickups paused. Top up with Fixigo.</p>}
            </Card>
            <div onClick={() => navigate("/portal/sell-pickups")} className="cursor-pointer">
              <Stat icon={Warning} label="Pickups To Accept" value={sell.pending_acceptance} accent="bg-amber-100 text-amber-700" />
            </div>
            <div onClick={() => navigate("/portal/sell-pickups")} className="cursor-pointer">
              <Stat icon={Clock} label="Pickups In Progress" value={sell.in_progress} accent="bg-violet-100 text-violet-700" />
            </div>
            <div onClick={() => navigate("/portal/sell-pickups")} className="cursor-pointer">
              <Stat icon={CheckCircle} label="Pickups Completed" value={sell.completed} accent="bg-green-100 text-green-700" />
            </div>
          </div>
        )}

        <Card className="rounded-sm border-border">
          <div className="px-6 py-4 border-b border-border flex items-center justify-between">
            <h3 className="font-heading font-semibold">Recent Jobs</h3>
            <Button variant="ghost" size="sm" className="rounded-sm" onClick={() => navigate("/portal/jobs")} data-testid="view-all-jobs">View all</Button>
          </div>
          <div className="divide-y divide-border" data-testid="recent-jobs">
            {d.recent_jobs.length === 0 && <p className="px-6 py-8 text-center text-sm text-muted-foreground">No jobs yet</p>}
            {d.recent_jobs.map((j) => (
              <div key={j.id} className="px-6 py-3 flex items-center justify-between text-sm">
                <div>
                  <p className="font-semibold">{j.service_type} · {j.product_category}</p>
                  <p className="text-xs text-muted-foreground">{j.customer_name} · {j.scheduled_date || "—"}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-mono">{fmtINR(j.amount)}</span>
                  <StatusBadge value={j.status === "in_progress" ? "pending" : (j.status === "completed" ? "complete" : j.status)} />
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </>
  );
}
